import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames/bind';

import {
  I18n,
  Checkbox,
  Button,
} from 'components';

import styles from './styles.pcss';

const cx = classNames.bind(styles);

const sort = (data, comparator, { reverse, dataField }) => {
  const predicate = reverse ? -1 : 1;

  return [...data].sort((a, b) => predicate * comparator(a, b, dataField));
};

const TableHeader = ({
  columns,
  nameAsTranslationKey = true,
  useCheckboxSelection = false,
  isAllSelected = false,
  onChangeSelection,
  sortable = false,
  sortingInfo = {},
  updateSorting,
}) => (
  <thead className={cx('header')}>
    <tr className={cx('row')}>
      {useCheckboxSelection
    && (
      <th className={cx('cell', 'checkboxCell')}>
        <Checkbox
          className={cx('checkbox')}
          checked={isAllSelected}
          handleChange={() => onChangeSelection(!isAllSelected)}
        />
      </th>
    )}
      {columns
        .filter(({ hidden }) => !hidden)
        .map(({
          name,
          width,
          dataField,
          hideOnMobile,
        }, i) => (
          <th
            key={`table-header-${dataField + i}`}
            style={{ width }}
            className={cx('cell', { sortableCell: sortable, hideOnMobile })}
            onClick={() => sortable && updateSorting({
              dataField,
              reverse: dataField === sortingInfo.dataField && !sortingInfo.reverse,
            })}
          >
            <span className={cx('columnTitle')}>
              {nameAsTranslationKey ? <I18n path={name} /> : name}
            </span>
            {sortable && sortingInfo && sortingInfo.dataField === dataField && (
            <span
              className={cx('arrow', {
                arrowUp: !sortingInfo.reverse,
                arrowDown: sortingInfo.reverse,
              })}
            />
            )}
          </th>
        ))}
    </tr>
  </thead>
);

TableHeader.propTypes = {
  columns: PropTypes.array,
  nameAsTranslationKey: PropTypes.bool,
  useCheckboxSelection: PropTypes.bool,
  isAllSelected: PropTypes.bool,
  onChangeSelection: PropTypes.func,
  sortable: PropTypes.bool,
  sortingInfo: PropTypes.object,
  updateSorting: PropTypes.func,
};

class TableBody extends PureComponent {
  getSortedData() {
    const {
      sortingInfo,
      comparator,
      sortable,
      data,
      showedPages,
      pageSize,
    } = this.props;

    const count = pageSize && (pageSize * showedPages);

    return sortable && sortingInfo && sortingInfo.dataField
      ? sort(data, comparator, sortingInfo).slice(0, count)
      : data && data.slice(0, count);
  }

  getRows = (rows, row, index) => {
    const {
      columns,
      uniqueIdField,
      selectionField,
      expandField,
      useCheckboxSelection,
      onChangeRowSelection,
      expandable,
      expandRenderer,
      toggleRow,
    } = this.props;

    const expandedRow = expandable && expandRenderer && expandRenderer(row, index);

    return [
      ...rows,
      <tr
        key={`table-row-${row[uniqueIdField]}-${index}`}
        className={cx('row')}
      >
        {useCheckboxSelection
          && (
          <td className={cx('cell', 'checkboxCell')}>
            {row[selectionField] !== undefined ? (
              <Checkbox
                className={cx('checkbox')}
                checked={row[selectionField]}
                handleChange={
                  () => onChangeRowSelection({
                    id: row[uniqueIdField],
                    isSelected: !row[selectionField],
                  })
                }
              />
            ) : null}
          </td>
          )}
        {columns
          .filter(({ hidden }) => !hidden)
          .map(({
            dataField,
            transformer,
            hideOnMobile,
            expandable: isColumnExpandable,
          }, i) => (
            <td
              key={`table-cell-${dataField + i}`}
              className={cx('cell', { hideOnMobile })}
            >
              <span
                role="presentation"
                onClick={() => isColumnExpandable && expandedRow && toggleRow({
                  id: row[uniqueIdField],
                  isExpanded: !row[expandField],
                })}
                className={cx({
                  rowExpander: isColumnExpandable && expandedRow,
                  unread: row.status === 'UNREAD',
                })}
              >
                {transformer
                  ? transformer(row, i)
                  : row[dataField]}
              </span>
            </td>
          ))}
      </tr>,
      expandedRow ? (
        <tr
          key={`table-expandable-row-${row[uniqueIdField]}-${index}`}
          className={cx('row', 'expandableRow', { hiddenRow: !row[expandField] })}
        >
          <td
            className={cx('cell')}
            colSpan={useCheckboxSelection ? columns.length + 1 : columns.length}
          >
            {expandedRow}
          </td>
        </tr>
      ) : null,
    ];
  };

  render() {
    return (
      <tbody className={cx('body')}>
        {this.getSortedData().reduce(this.getRows, [])}
      </tbody>
    );
  }
}

TableBody.defaultProps = {
  uniqueIdField: 'id',
  selectionField: 'selected',
  expandField: 'expanded',
  useCheckboxSelection: false,
  sortable: false,
  expandable: false,
  comparator(a, b, dataField) {
    if (a[dataField] < b[dataField]) {
      return -1;
    }

    if (a[dataField] > b[dataField]) {
      return 1;
    }

    return 0;
  },
};

TableBody.propTypes = {
  data: PropTypes.array,
  columns: PropTypes.array,
  uniqueIdField: PropTypes.string,
  selectionField: PropTypes.string,
  expandField: PropTypes.string,
  useCheckboxSelection: PropTypes.bool,
  onChangeRowSelection: PropTypes.func,
  sortingInfo: PropTypes.object,
  sortable: PropTypes.bool,
  comparator: PropTypes.func,
  expandable: PropTypes.bool,
  expandRenderer: PropTypes.func,
  toggleRow: PropTypes.func,
  showedPages: PropTypes.number,
  pageSize: PropTypes.number,
};

class Table extends PureComponent {
  constructor(props) {
    super(props);

    this.state = { showedPages: 1 };
  }

  isExpandable() {
    return this.props.config.columns
      .some((column) => column.expandable);
  }

  render() {
    const {
      className,
      data,
      config,
      sortingInfo,
      isAllRowsSelected,
      resetRowsSelection,
      changeRowSelection,
      toggleExpandableRow,
      updateSorting,
      expandRenderer,
    } = this.props;

    const { showedPages } = this.state;

    let renderCount = 0;
    if (config.pageSize && data) {
      const diff = data.length - (config.pageSize * showedPages);
      renderCount = diff < config.pageSize ? diff : config.pageSize;
    }

    return (
      <>
        <table className={cx('container', className)}>
          <TableHeader
            {...config}
            updateSorting={updateSorting}
            sortingInfo={sortingInfo}
            isAllSelected={isAllRowsSelected}
            onChangeSelection={resetRowsSelection}
          />
          <TableBody
            {...config}
            showedPages={showedPages}
            data={data}
            sortingInfo={sortingInfo}
            onChangeRowSelection={changeRowSelection}
            toggleRow={toggleExpandableRow}
            expandable={this.isExpandable()}
            expandRenderer={expandRenderer}
          />
        </table>
        {(renderCount > 0) && (
          <div className={cx('showMoreButtonContainer')}>
            <Button
              onClick={() => this.setState((prevState) => ({ showedPages: prevState.showedPages + 1 }))}
            >
              <I18n path="common.showMore" vars={{ count: renderCount }} />
            </Button>
          </div>
        )}
      </>
    );
  }
}

Table.propTypes = {
  className: PropTypes.any,
  data: PropTypes.array,
  config: PropTypes.object,
  sortingInfo: PropTypes.object,
  isAllRowsSelected: PropTypes.bool,
  resetRowsSelection: PropTypes.func,
  changeRowSelection: PropTypes.func,
  toggleExpandableRow: PropTypes.func,
  updateSorting: PropTypes.func,
  expandRenderer: PropTypes.func,
};

export default Table;
